import React from 'react';
import { Helmet } from 'react-helmet-async';
import { AppStoreBadge, BetaInviteSection } from '../components';
import { liveLanguages, liveLanguagesPhrase } from '../data/languages';
import { colors, playful } from '../styles/theme';
import { useIsMobile } from '../hooks';

const principles = [
  {
    number: '01',
    title: 'Start with words you can use.',
    description:
      'Food, family, friends, travel. Learn through everyday situations and phrases that belong in your next conversation.',
    color: '#FFF0DF',
    symbol: 'Hello!',
  },
  {
    number: '02',
    title: 'New language. Familiar letters.',
    description:
      'English meaning and transliteration help you start speaking without having to learn a new writing system first.',
    color: '#EAF6FF',
    symbol: 'Aa',
  },
  {
    number: '03',
    title: 'Give your voice some practice.',
    description:
      'Use the words you have learned in a conversation. Check a translation or ask for a hint when you get stuck.',
    color: '#F0EDFF',
    symbol: <img src="/conversation-icon.png" width={64} height={52} alt="" style={{ objectFit: 'contain' }} />,
  },
];

export function AboutPage() {
  const isMobile = useIsMobile();
  return (
    <div>
      <Helmet>
        <title>About Bhol | Learn Indian Languages Through Conversation</title>
        <meta
          name="description"
          content={`Learn ${liveLanguagesPhrase} with transliteration-first lessons, pronunciation practice, and conversations.`}
        />
        <link rel="canonical" href="https://bhol.app/about" />
      </Helmet>
      <section
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1.2fr 0.8fr',
          alignItems: 'center',
          gap: 28,
          padding: '36px 0 52px',
          textAlign: isMobile ? 'center' : 'left',
        }}
      >
        <div>
          <p style={{ color: playful.orange, fontWeight: 800 }}>
            A little about Bhol
          </p>
          <h1
            style={{ ...playful.heading, fontSize: 'clamp(2.4rem, 5vw, 4rem)' }}
          >
            Speak with family.
            <br />
            <span style={{ color: playful.orange }}>Talk with friends.</span>
          </h1>
          <p
            style={{
              color: colors.textLight,
              fontSize: 19,
              lineHeight: 1.65,
              maxWidth: 530,
              margin: '24px auto 0',
            }}
          >
            Bhol helps you practise the language you hear at home, on family calls,
            or when you travel.
          </p>
        </div>
        <div
          style={{
            background: '#FFF0DF',
            borderRadius: '45%',
            padding: 24,
            maxWidth: 340,
            width: '100%',
            margin: '0 auto',
          }}
        >
          <img
            src="/head-hathi-512.webp"
            width={512}
            height={512}
            alt="Meet Hathi, Bhol’s elephant mascot"
            fetchPriority="high"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </div>
      </section>
      <section
        style={{
          ...playful.card,
          padding: 'clamp(24px, 5vw, 48px)',
          background: '#EAF6FF',
        }}
      >
        <h2
          style={{ ...playful.heading, fontSize: 'clamp(1.8rem, 4vw, 2.7rem)' }}
        >
          Why we made Bhol
        </h2>
        <p
          style={{
            color: colors.textLight,
            fontSize: 18,
            lineHeight: 1.75,
            maxWidth: 760,
          }}
        >
          A call home. A conversation with a friend. Ordering your favourite
          food. These are the conversations our lessons are built around.
        </p>
        <p
          style={{
            color: colors.textLight,
            lineHeight: 1.75,
            maxWidth: 760,
            marginBottom: 0,
          }}
        >
          Whether you grew up hearing a language or are starting from scratch,
          you can learn vocabulary, listen to examples, and practise speaking in Bhol.
        </p>
      </section>
      <section style={{ padding: '56px 0' }}>
        <h2
          style={{
            ...playful.heading,
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.7rem)',
            marginBottom: 30,
          }}
        >
          How the lessons work
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
            gap: 20,
          }}
        >
          {principles.map((item) => (
            <article key={item.number} style={{ ...playful.card, padding: 24 }}>
              <div
                aria-hidden="true"
                style={{
                  background: item.color,
                  height: 112,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: 20,
                  padding: '24px 12px',
                  textAlign: 'center',
                  fontSize: 38,
                  fontWeight: 850,
                  color: colors.text,
                }}
              >
                {item.symbol}
              </div>
              <p
                style={{
                  color: playful.orange,
                  fontWeight: 800,
                  marginBottom: 10,
                }}
              >
                {item.number}
              </p>
              <h3 style={{ ...playful.heading, fontSize: 23 }}>{item.title}</h3>
              <p
                style={{
                  color: colors.textLight,
                  lineHeight: 1.7,
                  marginBottom: 0,
                }}
              >
                {item.description}
              </p>
            </article>
          ))}
        </div>
      </section>
      <section style={{ textAlign: 'center', padding: '28px 0 48px' }}>
        <h2
          style={{ ...playful.heading, fontSize: 'clamp(1.8rem, 4vw, 2.7rem)' }}
        >
          Choose from {liveLanguages.length} languages.
        </h2>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 10,
            marginTop: 26,
          }}
        >
          {liveLanguages.map((language) => (
            <span
              key={language.code}
              style={{
                ...playful.card,
                background: language.background,
                padding: '10px 16px',
                borderRadius: 16,
                fontWeight: 750,
              }}
            >
              {language.name}
            </span>
          ))}
        </div>
      </section>
      <section
        style={{
          ...playful.card,
          padding: 30,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 20,
          background: '#F0EDFF',
        }}
      >
        <div>
          <h2 style={{ ...playful.heading, fontSize: 26 }}>
            A little reading. A new perspective.
          </h2>
          <p style={{ color: colors.textLight, marginBottom: 0 }}>
            Explore practical language guides and everyday conversation tips.
          </p>
        </div>
        <a
          href="/blog"
          style={{
            ...playful.smallButton,
            textDecoration: 'none',
            display: 'inline-block',
          }}
        >
          Explore the blog →
        </a>
      </section>
      <BetaInviteSection analyticsLocation="about_before_bottom_cta" />
      <section
        style={{
          textAlign: 'center',
          padding: '36px 24px',
          ...playful.card,
          background: '#FFF0DF',
        }}
      >
        <h2 style={{ ...playful.heading, fontSize: 30, marginBottom: 24 }}>
          Let’s get talking.
        </h2>
        <AppStoreBadge location="about_bottom_cta" />
      </section>
    </div>
  );
}
export default AboutPage;
